import { formatCurrency, formatCurrencyCompact } from '@/shared/lib/currency';
import {
  BasicVerticalBarChart,
  basicVerticalBarBackgroundPlugin,
} from '@/shared/ui/charts';
import type { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { dimmed } from '../model/series-config';
import type { RevenueAnalysisSeriesView } from '../model/types';

type RevenueBarChartProps = {
  months: string[];
  series: RevenueAnalysisSeriesView[];
  hoveredKey: string | null;
  onHover: (key: string | null) => void;
};

/**
 * Revenue metric: one stacked bar per month, one stack segment per series.
 *
 * The grey full-height track behind each bar comes from
 * `basicVerticalBarBackgroundPlugin`, the same plugin the other bar charts use —
 * it is a chart affordance, not something this card invents.
 */
export const RevenueBarChart: FC<RevenueBarChartProps> = ({
  months,
  series,
  hoveredKey,
  onHover,
}) => {
  const data: ChartData<'bar'> = {
    labels: months.map((m) => dayjs(`${m}-01`).format('MMM YYYY')),
    datasets: series.map((s) => ({
      label: s.label,
      data: s.values,
      // Dimming every *other* series is what makes one stack segment stand out;
      // brightening the hovered one instead would just make the chart flash.
      backgroundColor:
        hoveredKey && hoveredKey !== s.key ? dimmed(s.color) : s.color,
      stack: 'revenue',
      barThickness: 28,
    })),
  };

  const options: ChartOptions<'bar'> = {
    // Index mode makes the whole month column one hover target, so a segment worth <1% of the
    // stack — a couple of pixels tall — is still reachable. Chart.js's bar default
    // (nearest/intersect) requires the cursor to land inside the segment rect, which made
    // Renaissance's Medicaid sliver impossible to hover (ST #175 comment 366). The segments keep
    // their real size; only the hit area grows.
    interaction: { mode: 'index', intersect: false },
    onHover: (event, _elements, chart) => {
      // The tooltip is column-wide, but the dimming still tracks a single series, so this
      // hit-tests separately. `intersect: false` picks the closest segment centre rather than
      // demanding a hit inside the rect, which is what the tiny segments could never satisfy.
      const native = event.native;
      if (!native) {
        onHover(null);
        return;
      }
      const nearest = chart.getElementsAtEventForMode(
        native,
        'nearest',
        { intersect: false },
        true
      );
      const index = nearest[0]?.datasetIndex;
      onHover(index === undefined ? null : (series[index]?.key ?? null));
    },
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: {
        stacked: true,
        ticks: {
          // The shared chart defaults to stepSize 50, which at revenue scale asks
          // for tens of thousands of ticks; undefined hands it back to Chart.js.
          stepSize: undefined,
          maxTicksLimit: 6,
          callback: (value) => formatCurrencyCompact(+value),
        },
      },
      // The shared bar chart declares a right-hand axis for its dual-axis use;
      // this chart has one scale, so it is switched off rather than inherited.
      y1: { display: false },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (item: TooltipItem<'bar'>) => {
            const value = item.parsed.y ?? 0;
            const total = series.reduce(
              (sum, s) => sum + (s.values[item.dataIndex] ?? 0),
              0
            );
            const amount = `${item.dataset.label}: ${formatCurrency(value)}`;
            if (total <= 0) return amount;
            const pct = (value / total) * 100;
            // Same convention as the KPI cards' share badge: a tiny-but-real
            // segment reads "<1%" rather than rounding down to a false 0%.
            const label = pct > 0 && pct < 1 ? '<1%' : `${Math.round(pct)}%`;
            return `${amount} (${label})`;
          },
        },
        // A month with no revenue for a series would otherwise add a "$0 (0%)" line to every
        // tooltip now that all series are listed at once. Exact zeroes only — a small non-zero
        // segment is precisely what this change exists to surface.
        filter: (item: TooltipItem<'bar'>) => (item.parsed.y ?? 0) !== 0,
      },
    },
  };

  return (
    <BasicVerticalBarChart
      data={data}
      options={options}
      plugins={[basicVerticalBarBackgroundPlugin]}
    />
  );
};
